import { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';

export function InstallPrompt() {
  const { deferredPrompt, platformInfo, handleInstall } = useInstallPrompt();
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if (platformInfo.isStandalone) return;

    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedDate = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedDate) / (1000 * 60 * 60 * 24);
    
    // Show again after 7 days
    if (dismissed && daysSinceDismissed < 7) return;

    if (deferredPrompt) {
      // Show after 30 seconds for Chrome/Edge
      setTimeout(() => {
        setShowPrompt(true);
      }, 30000);
    } else if (platformInfo.platform === 'ios') {
      // For iOS, show after 1 minute (no native prompt available)
      setTimeout(() => {
        setShowPrompt(true);
      }, 60000);
    }
  }, [deferredPrompt, platformInfo]);

  const handleInstallClick = async () => {
    const outcome = await handleInstall();
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    } else if (outcome === 'dismissed') {
      setShowPrompt(false);
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <Card className="fixed bottom-4 right-4 w-80 md:w-96 shadow-2xl z-50 animate-in slide-in-from-bottom-5">
      <CardContent className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            {platformInfo.platform === 'ios' ? (
              <Smartphone className="h-5 w-5 text-primary" />
            ) : (
              <Download className="h-5 w-5 text-primary" />
            )}
            <h3 className="font-semibold text-base">Install BuildCRM</h3>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 -mt-1 -mr-1"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          {platformInfo.platform === 'ios' 
            ? 'Add BuildCRM to your home screen for quick access, offline capabilities, and a native app experience.'
            : 'Install BuildCRM for faster access, offline capabilities, and a native app experience.'
          }
        </p>
        
        <div className="flex gap-2">
          {deferredPrompt ? (
            <Button onClick={handleInstallClick} className="flex-1" size="default">
              <Download className="mr-2 h-4 w-4" />
              Install Now
            </Button>
          ) : (
            <Button onClick={() => window.location.href = '/install'} className="flex-1" size="default">
              <Smartphone className="mr-2 h-4 w-4" />
              View Instructions
            </Button>
          )}
          <Button variant="outline" onClick={handleDismiss} size="default">
            Later
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
